import moment from "moment";
import {
  ComplianceProgramNotificationType,
  TimelineEntryKind,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";

import {
  TimelineRow,
  predecessorOf,
  stepNames,
  stepOnly,
} from "./compliance-program-timeline-vocabulary";

/**
 * The plan of a review as the stages an admin reads in order: each day of the
 * calendar, with the messages that people set off placed between them.
 *
 * Kept apart from the component that draws it, so the order can be tested alone.
 */

/** One day of the calendar, with everything the plan puts on it. */
export type DateStage = {
  kind: "date";
  date: string;
  isKickoff: boolean;
  /** The kickoff row's own words, "Annual review 2026 starts", on that day only. */
  startLabel: string | null;
  isPast: boolean;
  /** Steps that open on the day itself. */
  opens: string[];
  due: string[];
  /** Steps that open for each person as a step due today is done. */
  opensNext: string[];
  /** For each step in `opensNext`, the step it waits on. */
  opensNextAfter: Record<string, string>;
  /** Messages that go out on the day: automatic ones, then the admin's reminders. */
  messages: TimelineRow[];
  /**
   * Every other row the day carries, in its own words: the line saying a
   * recurring review makes its next copy, and the whole plan of an ongoing
   * review. Dropping them would draw a day that does less than it does.
   */
  other: TimelineRow[];
};

/** One message that goes out as people act. */
export type TriggerStage = { kind: "trigger"; row: TimelineRow };

export type ProgressionStage = DateStage | TriggerStage;

/**
 * Where a message falls in a review, by the backend catalog's own order. A row
 * with none, such as an admin's reminder, comes after the automatic messages.
 */
const sendOrder = (row: TimelineRow) =>
  row.sendOrder ?? Number.MAX_SAFE_INTEGER;

/**
 * Where a row falls within its day: a message by when a review sends it, a step
 * by where it sits in a review. The plan sorts a day's rows by label, which reads
 * "Peer review" before "Self review" and "Delivery" before "Calibration".
 */
const dayOrder = (row: TimelineRow, steps: string[]) => {
  if (
    row.kind === TimelineEntryKind.Notification ||
    row.kind === TimelineEntryKind.Reminder
  ) {
    return sendOrder(row);
  }
  return row.step ? steps.indexOf(row.step) : steps.length;
};

/**
 * The stages in the order they happen.
 *
 * A trigger goes before the first day on or after its date, which is the due
 * date it is expected by. Never before kickoff: nothing is set off before the
 * review starts.
 */
export const progressionOf = (
  rows: readonly TimelineRow[],
  label: UseLabelType,
): ProgressionStage[] => {
  const kickoffDate = rows.find(
    (row) => row.kind === TimelineEntryKind.Kickoff,
  )?.date;
  const byDate = new Map<string, DateStage>();
  const dayOf = (row: TimelineRow) => {
    const existing = byDate.get(row.date);
    if (existing) {
      return existing;
    }
    const day: DateStage = {
      kind: "date",
      date: row.date,
      isKickoff: row.date === kickoffDate,
      startLabel: null,
      isPast: row.isPast,
      opens: [],
      due: [],
      opensNext: [],
      opensNextAfter: {},
      messages: [],
      other: [],
    };
    byDate.set(row.date, day);
    return day;
  };

  const triggers: TriggerStage[] = [];
  // The review's steps in order, built once rather than in every comparison.
  const steps = Object.keys(stepNames(label));
  [...rows]
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || dayOrder(a, steps) - dayOrder(b, steps),
    )
    .forEach((row) => {
      if (row.kind === TimelineEntryKind.Kickoff) {
        dayOf(row).startLabel = row.label;
      } else if (row.kind === TimelineEntryKind.StepOpens) {
        const day = dayOf(row);
        const name = stepOnly(row, label);
        const after = predecessorOf(row, label);
        if (after) {
          day.opensNext.push(name);
          day.opensNextAfter[name] = after;
        } else {
          day.opens.push(name);
        }
      } else if (row.kind === TimelineEntryKind.StepDue) {
        dayOf(row).due.push(stepOnly(row, label));
      } else if (row.kind === TimelineEntryKind.Notification) {
        if (row.waitsOnAction) {
          triggers.push({ kind: "trigger", row });
        } else {
          dayOf(row).messages.push(row);
        }
      } else if (row.kind === TimelineEntryKind.Reminder) {
        dayOf(row).messages.push(row);
      } else {
        dayOf(row).other.push(row);
      }
    });

  const stages: ProgressionStage[] = [...byDate.values()].sort((a, b) =>
    a.date.localeCompare(b.date),
  );
  triggers
    .sort(
      (a, b) =>
        a.row.date.localeCompare(b.row.date) ||
        sendOrder(a.row) - sendOrder(b.row),
    )
    .forEach((trigger) => {
      const firstAllowed = stages.findIndex(
        (stage) => stage.kind === "date" && stage.isKickoff,
      );
      const at = stages.findIndex(
        (stage, index) =>
          index > firstAllowed &&
          stage.kind === "date" &&
          stage.date >= trigger.row.date,
      );
      stages.splice(at === -1 ? stages.length : at, 0, trigger);
    });
  return stages;
};

const listed = (names: string[]) => names.join(", ");

/** "Self review, Peer review open" / "Manager review opens". */
export const opensSentence = (names: string[]) =>
  `${listed(names)} ${names.length === 1 ? "opens" : "open"}`;

export const dueSentence = (names: string[]) => `${listed(names)} due`;

/**
 * "All admins who opted in" → "Sent to all admins who opted in". A row whose
 * recipients nobody can know yet says that instead, since it is sent to nobody
 * in particular yet.
 */
export const sentTo = (who: string, recipientsKnown: boolean) =>
  recipientsKnown
    ? `Sent to ${who.charAt(0).toLowerCase()}${who.slice(1)}`
    : "Recipients not known yet";

/**
 * One thing that happens: "Self review opens", then when. `date` and `trigger` are the two answers to
 * "when?", and exactly one of them is set.
 */
export type ProgressionEvent = {
  key: string;
  kind: "start" | "opens" | "due" | "message" | "other";
  title: string;
  date: string | null;
  trigger: string | null;
  /**
   * What goes out with it. A message event is its own one message; a step that
   * opens carries the message telling people it is open.
   */
  messages: TimelineRow[];
  /** The plan row behind an ongoing or recurring review's own line. */
  row: TimelineRow | null;
  isPast: boolean;
};

/**
 * The messages that say a step is open, and so are the step opening rather than
 * a second thing on the same day. The package messages follow a package, not a
 * step, and stay events of their own.
 */
const openingMessages: ComplianceProgramNotificationType[] = [
  ComplianceProgramNotificationType.EngagementSurveyReady,
  ComplianceProgramNotificationType.IcSelfAssessment,
  ComplianceProgramNotificationType.UpwardAssessment,
  ComplianceProgramNotificationType.PeerAssessment,
  ComplianceProgramNotificationType.ManagerAssessment,
];

/**
 * The same stages, unfolded into one event each, in the same order.
 *
 * A step that opens for each person as an earlier one is done is a trigger here,
 * not a date: it has no day of its own, as a message somebody sets off has none.
 * It stands before the earlier step's due date, inside the window it happens in,
 * as the triggers in the stages do.
 *
 * Each step opens as an event of its own, and the message saying it is open joins
 * it: "Self review opens" and "Self review ready" are one moment, not two. Only
 * when both happen the same way — on the same day, or both set off by people — so
 * a message with a "when" of its own never loses it.
 */
export const eventsOf = (
  stages: ProgressionStage[],
  label: UseLabelType,
): ProgressionEvent[] => {
  // Something people set off keeps firing after the due date it is expected by:
  // a late self review still unlocks its manager review. So it only reads as
  // past once every day of the review has.
  const reviewOver = stages.every(
    (stage) => stage.kind !== "date" || stage.isPast,
  );
  const raw = stages.flatMap((stage, index): ProgressionEvent[] => {
    if (stage.kind === "trigger") {
      return [
        {
          key: `trigger-${index}-${stage.row.label}`,
          kind: "message",
          title: stage.row.label,
          date: null,
          // The backend writes this beside its note, short enough to stand
          // where a date would.
          trigger: stage.row.triggerLabel ?? "As people act",
          messages: [stage.row],
          row: null,
          isPast: reviewOver,
        },
      ];
    }
    const on = (
      kind: ProgressionEvent["kind"],
      title: string,
      { messages = [], row = null }: Partial<ProgressionEvent> = {},
    ): ProgressionEvent => ({
      key: `${stage.date}-${kind}-${title}`,
      kind,
      title,
      date: stage.date,
      trigger: null,
      messages,
      row,
      isPast: messages[0]?.isPast ?? row?.isPast ?? stage.isPast,
    });
    return [
      ...(stage.isKickoff && stage.startLabel
        ? [on("start", stage.startLabel)]
        : []),
      ...stage.opens.map((name) => on("opens", opensSentence([name]))),
      ...stage.messages.map((row) =>
        on("message", row.label, { messages: [row] }),
      ),
      ...stage.opensNext.map(
        (name): ProgressionEvent => ({
          key: `${stage.date}-opens-next-${name}`,
          kind: "opens",
          title: opensSentence([name]),
          date: null,
          trigger: `After each ${stage.opensNextAfter[name].toLowerCase()}`,
          messages: [],
          row: null,
          isPast: reviewOver,
        }),
      ),
      ...(stage.due.length > 0 ? [on("due", dueSentence(stage.due))] : []),
      ...stage.other.map((row) => on("other", row.label, { row })),
    ];
  });

  // Fold each opening message into the step it opens.
  const names = stepNames(label);
  const opening = (event: ProgressionEvent) => {
    const row = event.messages[0];
    const step = row?.step;
    if (
      event.kind !== "message" ||
      !step ||
      !row.notificationType ||
      !openingMessages.includes(row.notificationType)
    ) {
      return null;
    }
    return raw.find(
      (candidate) =>
        candidate.kind === "opens" &&
        candidate.title === opensSentence([names[step]]) &&
        (candidate.date !== null
          ? candidate.date === event.date
          : event.trigger !== null),
    );
  };
  const events = raw.flatMap((event) => {
    const opens = opening(event);
    if (!opens) {
      return [event];
    }
    opens.messages.push(...event.messages);
    // The message says exactly what sets it off, where the step only knows what
    // it waits on: a manager review unlocks on the self review *and*
    // calibration, when the review has both.
    if (opens.trigger !== null && event.trigger !== null) {
      opens.trigger = event.trigger;
    }
    opens.isPast = opens.isPast && event.isPast;
    return [];
  });
  // Two rows can share a day, a kind and a label, such as the same reminder
  // sent once by email and once by Slack, so the place in the list is part of
  // what tells them apart.
  return events.map((event, index) => ({
    ...event,
    key: `${event.key}-${index}`,
  }));
};

/**
 * What the date column says beside each event: its day, on every row that has
 * one, so a row reads on its own without looking up the list for its date. A
 * row people set off has no day, and says nothing there.
 *
 * The year goes when every day in the plan shares one, which is nearly always: a
 * single cycle rarely crosses New Year, and when it does the year is the point.
 */
export const dateColumnLabels = (events: ProgressionEvent[]) => {
  const years = new Set(
    events.flatMap((event) => (event.date ? [event.date.slice(0, 4)] : [])),
  );
  // Says whether the labels carry the year, so the column can be wide enough
  // for "Dec 15, 2026" on one line.
  const withYear = years.size > 1;
  const format = withYear ? "MMM D, YYYY" : "MMM D";
  return {
    withYear,
    labels: events.map((event) =>
      event.date ? moment(event.date).format(format) : null,
    ),
  };
};
